package com.rajen.angrejidictionary.components.widgets

import androidx.compose.runtime.*
import com.rajen.angrejidictionary.analytics.Umami
import com.rajen.angrejidictionary.domain.model.*
import com.rajen.angrejidictionary.presentation.state.WordUiState
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private val TitleColor = Color.rgb(0xC0, 0x39, 0x2B)
private val PrimaryText = Color.rgb(0x1A, 0x00, 0x00)
private val SecondaryText = Color.rgb(0x7B, 0x4F, 0x3A)
private val MutedText = Color.rgb(0xB0, 0x7A, 0x66)
private val SectionBg = Color.rgba(255, 255, 255, 0.38f)
private val BadgeBg = Color.rgba(255, 255, 255, 0.55f)
private val PhrasalBg = Color.rgba(255, 255, 255, 0.45f)

val WordCardStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Color.rgb(0xF1, 0xC6, 0xA6))
            .borderRadius(28.px)
            .padding(1.75.cssRem)
            .maxWidth(520.px)
            .fillMaxWidth()
            .overflow(Overflow.Hidden)
            .position(Position.Relative)
    }
    hover { Modifier.transform { scale(1.015) } }
}

enum class WordTab { Meaning, Forms, Usage, Grammar }

private fun wordPagePath(word: String): String {
    val display = word.trim().replaceFirstChar { it.uppercaseChar() }
    val encoded = js("encodeURIComponent(display)") as String
    return "/word/$encoded"
}

@Composable
fun WordCard(state: WordUiState, modifier: Modifier = Modifier) {
    var activeTab by remember { mutableStateOf(WordTab.Meaning) }

    Column(
        WordCardStyle.toModifier().then(modifier),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // WOTD header
        Span(
            attrs = Modifier.fontSize(0.65.cssRem).fontWeight(700)
                .letterSpacing(3.px).color(TitleColor).toAttrs()
        ) { Text("WORD OF THE DAY") }

        // Decorative divider
        Row(
            Modifier.fillMaxWidth().margin(topBottom = 0.6.cssRem),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(Modifier.flex(1).height(1.px).backgroundColor(Color.rgba(192, 57, 43, 0.22f)))
            repeat(3) {
                Box(
                    Modifier.size(5.px).borderRadius(50.percent)
                        .backgroundColor(Color.rgba(192, 57, 43, 0.35f))
                        .margin(leftRight = 4.px)
                )
            }
            Box(Modifier.flex(1).height(1.px).backgroundColor(Color.rgba(192, 57, 43, 0.22f)))
        }

        when (state) {
            WordUiState.Loading -> LoadingContent()
            is WordUiState.NotFound -> NotFoundContent(state.term)
            is WordUiState.Error -> ErrorContent(state.message)
            is WordUiState.Success -> {
                val word = state.word
                LaunchedEffect(word.englishWord) {
                    Umami.trackWordView(word.englishWord)
                }
                // English word
                Span(
                    attrs = Modifier.fontSize(2.8.cssRem).fontWeight(700)
                        .color(PrimaryText).margin(bottom = 0.6.cssRem).toAttrs()
                ) {
                    Text(word.englishWord)
                }

                // Badges: POS + IPA
                Row(Modifier.gap(7.px).margin(bottom = 0.9.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    Badge(word.partOfSpeech.uppercase(), isIpa = false)
                    Badge("/ˈrɪp.əl/", isIpa = true)
                }

                // Hide the label when the server didn't send a count (e.g. word of the day) or it is 0.
                val viewCount = word.viewCount?.takeIf { it > 0 }

                // Gujarati pronunciation
                Span(
                    attrs = Modifier.fontSize(1.45.cssRem).fontWeight(500)
                        .color(SecondaryText)
                        .margin(bottom = if (viewCount != null) 0.4.cssRem else 1.3.cssRem).toAttrs()
                ) {
                    Text(word.pronunciationGujarati)
                }

                // View count (word detail page only)
                if (viewCount != null) {
                    Span(
                        attrs = Modifier.fontSize(0.8.cssRem).color(MutedText)
                            .margin(bottom = 1.3.cssRem).toAttrs()
                    ) {
                        Text("👁 $viewCount ${if (viewCount == 1L) "view" else "views"}")
                    }
                }

                // Tab bar
                TabBar(activeTab) { selected ->
                    activeTab = selected
                    Umami.trackWordTabSelect(selected.name)
                }

                // Tab content
                when (activeTab) {
                    WordTab.Meaning -> MeaningPane(word)
                    WordTab.Forms -> FormsPane(word.wordForms)
                    WordTab.Usage -> UsagePane(word)
                    WordTab.Grammar -> GrammarPane(word)
                }

                /*                // Footer buttons
                                Row(
                                    Modifier.margin(top = 1.3.cssRem).gap(8.px),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    OutlineButton("🔊 Pronounce") {}
                                    PrimaryButton("↗ Share") {}
                                }*/
            }
        }
    }
}

// ── Sub-composables ──────────────────────────────────────────────────────────

@Composable
private fun TabBar(active: WordTab, onSelect: (WordTab) -> Unit) {
    Row(
        Modifier.fillMaxWidth().backgroundColor(Color.rgba(255, 255, 255, 0.3f))
            .borderRadius(12.px).padding(4.px).margin(bottom = 1.cssRem).gap(4.px)
    ) {
        WordTab.entries.forEach { tab ->
            val isActive = tab == active
            Box(
                Modifier.flex(1).borderRadius(9.px)
                    .backgroundColor(if (isActive) Color.rgba(255, 255, 255, 0.7f) else Color.rgba(0, 0, 0, 0))
                    .padding(topBottom = 7.px)
                    .cursor(Cursor.Pointer)
                    .onClick { onSelect(tab) },
                contentAlignment = Alignment.Center
            ) {
                Span(
                    attrs = Modifier.fontSize(0.68.cssRem).fontWeight(700)
                        .color(if (isActive) Color.rgb(0x7A, 0x1B, 0x1B) else TitleColor)
                        .letterSpacing(0.4.px).toAttrs()
                ) {
                    Text(tab.name)
                }
            }
        }
    }
}

@Composable
private fun MeaningPane(word: Word) {
    // Meanings
    CardSection("Meanings") {
        word.meanings.forEach { m ->
            Row(Modifier.margin(bottom = 0.4.cssRem).gap(8.px), verticalAlignment = Alignment.Top) {
                Box(
                    Modifier.size(6.px).borderRadius(50.percent)
                        .backgroundColor(TitleColor).margin(top = 6.px).flexShrink(0)
                )
                Span(attrs = Modifier.fontSize(0.94.cssRem).color(PrimaryText).toAttrs()) { Text(m) }
            }
        }
    }
    // Example (first word form)
    word.wordForms.firstOrNull()?.let { f ->
        if (f.exampleSentence.isNotBlank()) {
            CardSection("Example") {
                P(
                    attrs = Modifier.fontSize(0.92.cssRem).color(PrimaryText)
                        .fontStyle(FontStyle.Italic).margin(bottom = 0.3.cssRem).toAttrs()
                ) {
                    Text("\"${f.exampleSentence}\"")
                }
                P(
                    attrs = Modifier.fontSize(0.83.cssRem).color(SecondaryText)
                        .margin(bottom = 0.15.cssRem).toAttrs()
                ) { Text(f.examplePronunciationGujarati) }
                P(attrs = Modifier.fontSize(0.83.cssRem).color(MutedText).toAttrs()) { Text(f.exampleMeaningGujarati) }
            }
        }
    }
    // Similar words
    if (word.similarWords.isNotEmpty()) {
        CardSection("Similar words") {
            Row(Modifier.gap(6.px).flexWrap(FlexWrap.Wrap)) {
                word.similarWords.forEach { w ->
                    Chip("${w.word} — ${w.meanings.firstOrNull() ?: ""}", w.word, isOpposite = false)
                }
            }
        }
    }
    // Opposite words
    if (word.oppositeWords.isNotEmpty()) {
        CardSection("Opposite words") {
            Row(Modifier.gap(6.px).flexWrap(FlexWrap.Wrap)) {
                word.oppositeWords.forEach { w ->
                    Chip("${w.word} — ${w.meanings.firstOrNull() ?: ""}", w.word, isOpposite = true)
                }
            }
        }
    }
}

@Composable
private fun FormsPane(forms: List<WordForm>) {
    forms.forEach { f ->
        Box(
            Modifier.fillMaxWidth().backgroundColor(PhrasalBg).borderRadius(12.px)
                .padding(0.7.cssRem, 0.85.cssRem).margin(bottom = 0.55.cssRem)
        ) {
            Column(Modifier.fillMaxWidth()) {
                Row(Modifier.gap(8.px).margin(bottom = 0.35.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    Span(
                        attrs = Modifier.fontSize(1.cssRem).fontWeight(700).color(PrimaryText).toAttrs()
                    ) { Text(f.word) }
                    Badge(f.partOfSpeech.uppercase(), isIpa = false)
                }
                Span(
                    attrs = Modifier.fontSize(0.85.cssRem).color(SecondaryText)
                        .margin(bottom = 0.4.cssRem).toAttrs()
                ) { Text(f.pronunciationGujarati) }
                Row(Modifier.gap(6.px).flexWrap(FlexWrap.Wrap).margin(bottom = 0.4.cssRem)) {
                    f.meanings.forEach { m ->
                        Box(
                            Modifier.backgroundColor(Color.rgba(255, 255, 255, 0.5f)).borderRadius(999.px)
                                .padding(topBottom = 2.px, leftRight = 9.px)
                        ) {
                            Span(attrs = Modifier.fontSize(0.8.cssRem).color(PrimaryText).toAttrs()) { Text(m) }
                        }
                    }
                }
                if (f.exampleSentence.isNotBlank()) {
                    Span(
                        attrs = Modifier.fontSize(0.82.cssRem).color(Color.rgb(0x4A, 0x20, 0x10))
                            .fontStyle(FontStyle.Italic).toAttrs()
                    ) { Text("\"${f.exampleSentence}\"") }
                }
            }
        }
    }
}

@Composable
private fun UsagePane(word: Word) {
    word.usage?.let { u ->
        CardSection("When to use") {
            UsageRow("ક્યારે", u.whenGujarati)
            UsageRow("ક્યાં", u.whereGujarati)
            UsageRow("શા માટે", u.whyGujarati)
        }
    }
    if (word.phrasals.isNotEmpty()) {
        CardSection("Phrasal verbs") {
            word.phrasals.forEach { p ->
                Box(
                    Modifier.fillMaxWidth().backgroundColor(PhrasalBg).borderRadius(12.px)
                        .padding(0.7.cssRem, 0.85.cssRem).margin(bottom = 0.55.cssRem)
                ) {
                    Column(Modifier.fillMaxWidth()) {
                        Span(
                            attrs = Modifier.fontSize(1.cssRem).fontWeight(700)
                                .color(PrimaryText).margin(bottom = 0.25.cssRem).toAttrs()
                        ) { Text(p.form) }
                        Span(
                            attrs = Modifier.fontSize(0.84.cssRem).color(SecondaryText)
                                .margin(bottom = 0.3.cssRem).toAttrs()
                        ) {
                            Text("${p.pronunciationGujarati} — ${p.meanings.joinToString(", ")}")
                        }
                        p.examples.forEach { ex ->
                            Span(
                                attrs = Modifier.fontSize(0.82.cssRem).color(Color.rgb(0x4A, 0x20, 0x10))
                                    .fontStyle(FontStyle.Italic).margin(top = 0.3.cssRem).toAttrs()
                            ) {
                                Text("\"${ex.sentence}\"")
                            }
                            Span(
                                attrs = Modifier.fontSize(0.8.cssRem).color(MutedText)
                                    .margin(top = 0.15.cssRem).toAttrs()
                            ) { Text(ex.meaningGujarati) }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun GrammarPane(word: Word) {
    if (word.tenses.isNotEmpty()) {
        CardSection("Tenses") {
            // 2-column grid via CSS grid
            Div(
                attrs = Modifier
                    .display(DisplayStyle.Grid)
                    .gridTemplateColumns {
                        repeat(2) { size(1.fr) }
                    }
                    .gap(6.px).toAttrs()
            ) {
                word.tenses.forEach { t ->
                    Box(
                        Modifier.backgroundColor(PhrasalBg).borderRadius(10.px)
                            .padding(0.5.cssRem, 0.7.cssRem)
                    ) {
                        Column {
                            Span(
                                attrs = Modifier.fontSize(0.62.cssRem).fontWeight(700)
                                    .letterSpacing(1.px).color(MutedText).toAttrs()
                            ) {
                                Text(t.type.uppercase())
                            }
                            Span(
                                attrs = Modifier.fontSize(0.92.cssRem).fontWeight(600)
                                    .color(PrimaryText).margin(top = 2.px).toAttrs()
                            ) {
                                Text(t.word)
                            }
                        }
                    }
                }
            }
        }
    }
    if (word.tenseRule.isNotBlank()) {
        CardSection("Rule") {
            Span(attrs = Modifier.fontSize(0.88.cssRem).color(PrimaryText).lineHeight(1.6).toAttrs()) {
                Text(word.tenseRule)
            }
        }
    }
}

// ── Reusable atoms ───────────────────────────────────────────────────────────

@Composable
private fun CardSection(title: String, content: @Composable ColumnScope.() -> Unit) {
    Column(
        Modifier.fillMaxWidth().backgroundColor(SectionBg).borderRadius(16.px)
            .padding(0.95.cssRem, 1.05.cssRem).margin(bottom = 0.8.cssRem)
    ) {
        Span(
            attrs = Modifier.fontSize(0.65.cssRem).fontWeight(700).letterSpacing(2.px)
                .color(TitleColor).margin(bottom = 0.55.cssRem).toAttrs()
        ) {
            Text(title.uppercase())
        }
        content()
    }
}

@Composable
private fun Badge(label: String, isIpa: Boolean) {
    Box(
        Modifier.backgroundColor(if (isIpa) Color.rgba(192, 57, 43, 0.12f) else BadgeBg)
            .borderRadius(999.px).padding(topBottom = 3.px, leftRight = 13.px)
    ) {
        Span(
            attrs = Modifier.fontSize(0.66.cssRem).fontWeight(700)
                .letterSpacing(if (isIpa) 0.5.px else 1.4.px)
                .color(if (isIpa) Color.rgb(0x92, 0x2B, 0x21) else TitleColor).toAttrs()
        ) {
            Text(label)
        }
    }
}

@Composable
private fun Chip(label: String, word: String, isOpposite: Boolean) {
    Link(
        path = wordPagePath(word),
        modifier = Modifier
            .backgroundColor(if (isOpposite) Color.rgba(192, 57, 43, 0.1f) else Color.rgba(255, 255, 255, 0.5f))
            .borderRadius(999.px).padding(topBottom = 5.px, leftRight = 13.px)
            .border(
                1.px,
                LineStyle.Solid,
                if (isOpposite) Color.rgba(192, 57, 43, 0.25f) else Color.rgba(192, 57, 43, 0.2f)
            )
            .textDecorationLine(TextDecorationLine.None)
    ) {
        Span(
            attrs = Modifier.fontSize(0.82.cssRem)
                .color(if (isOpposite) Color.rgb(0x7A, 0x1B, 0x1B) else Color.rgb(0x5A, 0x2D, 0x1A)).toAttrs()
        ) { Text(label) }
    }
}

@Composable
private fun UsageRow(key: String, value: String) {
    Row(Modifier.gap(8.px).margin(bottom = 0.5.cssRem), verticalAlignment = Alignment.Top) {
        Span(
            attrs = Modifier.fontSize(0.62.cssRem).fontWeight(700).letterSpacing(1.px)
                .color(TitleColor).minWidth(46.px).margin(top = 2.px).toAttrs()
        ) {
            Text(key)
        }
        Span(attrs = Modifier.fontSize(0.87.cssRem).color(PrimaryText).lineHeight(1.5).toAttrs()) {
            Text(value)
        }
    }
}

@Composable
private fun OutlineButton(label: String, onClick: () -> Unit) {
    Box(
        Modifier.backgroundColor(Color.rgba(255, 255, 255, 0.55f)).borderRadius(999.px)
            .border(1.5.px, LineStyle.Solid, Color.rgba(192, 57, 43, 0.3f))
            .padding(topBottom = 8.px, leftRight = 20.px).cursor(Cursor.Pointer).onClick { onClick() }
    ) {
        Span(attrs = Modifier.fontSize(0.83.cssRem).fontWeight(600).color(TitleColor).toAttrs()) { Text(label) }
    }
}

@Composable
private fun PrimaryButton(label: String, onClick: () -> Unit) {
    Box(
        Modifier.backgroundColor(TitleColor).borderRadius(999.px)
            .padding(topBottom = 8.px, leftRight = 20.px).cursor(Cursor.Pointer).onClick { onClick() }
    ) {
        Span(
            attrs = Modifier.fontSize(0.83.cssRem).fontWeight(600)
                .color(Color.rgb(0xFF, 0xFF, 0xFF)).toAttrs()
        ) { Text(label) }
    }
}

@Composable
private fun LoadingContent() {
    Column(
        Modifier.fillMaxWidth().margin(top = 3.cssRem),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Span(attrs = Modifier.fontSize(2.cssRem).toAttrs()) { Text("📖") }
        Box(Modifier.margin(top = 1.cssRem)) { Text("Loading today's word...") }
    }
}

@Composable
private fun NotFoundContent(term: String) {
    val ctx = rememberPageContext()
    val displayTerm = term.trim().replaceFirstChar { it.uppercaseChar() }

    Column(
        Modifier.fillMaxWidth().margin(top = 2.2.cssRem),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Span(attrs = Modifier.fontSize(2.4.cssRem).toAttrs()) { Text("📚") }

        Span(
            attrs = Modifier.fontSize(1.3.cssRem).fontWeight(700)
                .color(PrimaryText).margin(top = 1.cssRem).toAttrs()
        ) { Text("Not Listed Yet") }

        Span(
            attrs = Modifier.fontSize(0.95.cssRem).color(SecondaryText)
                .textAlign(TextAlign.Center).lineHeight(1.6)
                .margin(top = 0.6.cssRem).maxWidth(340.px).toAttrs()
        ) {
            Text("\"$displayTerm\" isn't in our dictionary yet. We're adding new words all the time, so check back soon, or let us know if you'd like to see it added.")
        }

        Row(
            Modifier.margin(top = 1.6.cssRem).gap(10.px).flexWrap(FlexWrap.Wrap),
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically
        ) {
            PillButton(label = "🏠 Go to Home", filled = true) {
                Umami.track("word-not-found-go-home")
                ctx.router.navigateTo("/")
            }

            PillButton(label = "💬 Suggest This Word", filled = false, path = "/contact") {
                Umami.trackNavClick("/contact", "word-not-found")
            }
        }
    }
}

@Composable
private fun PillButton(
    label: String,
    filled: Boolean,
    path: String? = null,
    onClick: () -> Unit
) {
    val shape = Modifier
        .height(38.px)
        .borderRadius(999.px)
        .padding(leftRight = 20.px)
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .justifyContent(JustifyContent.Center)
        .cursor(Cursor.Pointer)
        .then(
            if (filled) {
                Modifier.backgroundColor(TitleColor)
            } else {
                Modifier
                    .backgroundColor(Color.rgba(255, 255, 255, 0.55f))
                    .border(1.5.px, LineStyle.Solid, Color.rgba(192, 57, 43, 0.3f))
            }
        )

    val textColor = if (filled) Color.rgb(0xFF, 0xFF, 0xFF) else TitleColor
    val label_: @Composable () -> Unit = {
        Span(
            attrs = Modifier.fontSize(0.83.cssRem).fontWeight(600)
                .lineHeight(1).color(textColor).toAttrs()
        ) { Text(label) }
    }

    if (path != null) {
        Link(
            path = path,
            modifier = shape.textDecorationLine(TextDecorationLine.None).onClick { onClick() }
        ) { label_() }
    } else {
        Box(shape.onClick { onClick() }) { label_() }
    }
}

@Composable
private fun ErrorContent(message: String) {
    val ctx = rememberPageContext()

    Column(
        Modifier.fillMaxWidth().margin(top = 3.cssRem),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Span(attrs = Modifier.fontSize(2.cssRem).toAttrs()) { Text("⚠️") }
        Box(Modifier.margin(top = 1.cssRem).textAlign(TextAlign.Center)) { Text(message) }

        Box(
            Modifier
                .margin(top = 1.4.cssRem)
                .backgroundColor(TitleColor)
                .borderRadius(999.px)
                .padding(topBottom = 9.px, leftRight = 22.px)
                .cursor(Cursor.Pointer)
                .onClick {
                    Umami.track("word-error-go-home")
                    ctx.router.navigateTo("/")
                }
        ) {
            Span(
                attrs = Modifier.fontSize(0.85.cssRem).fontWeight(700)
                    .color(Color.rgb(0xFF, 0xFF, 0xFF)).toAttrs()
            ) {
                Text("Go to Home")
            }
        }
    }
}